import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useLocalStorage } from '../useLocalStorage';
import VirtualKeyboard from '../keyboard/VirtualKeyboard';
import Confetti from '../confetti/Confetti';
import styles from './CrosswordStep.module.css';

export interface CrosswordStepProps {
    clues: CrosswordClue[];
    metaPhrase: string;
    metaMap?: Record<string, number[]>;
    // injected
    storageKey: string;
    initiallySolved?: boolean;
    disabled?: boolean;
    onSolved?: () => void;
}

export interface CrosswordClue {
    id: string;
    number: number;
    clue: string;
    answer: string;
    startRow: number;
    startCol: number;
    direction: 'across' | 'down';
}

type Persisted = {
    answers: Record<string, string>; // id -> normalized answer
    solvedIds: string[];
    status: 'playing' | 'won';
};

type CellState = {
    letter: string;
    isBlack: boolean;
    number?: number;
    acrossClue?: string;
    downClue?: string;
};

type Direction = 'across' | 'down';

export const CrosswordStep: React.FC<CrosswordStepProps> = ({
    clues,
    metaPhrase,
    metaMap,
    storageKey,
    initiallySolved = false,
    disabled = false,
    onSolved,
}) => {
    const [currentDirection, setCurrentDirection] = useState<Direction>('across');
    const [selectedCell, setSelectedCell] = useState<{ row: number; col: number } | null>(null);
    const [highlightedClue, setHighlightedClue] = useState<string | null>(null);
    const [showConfetti, setShowConfetti] = useState(false);

    const defaultState: Persisted = useMemo(() => ({
        answers: {},
        solvedIds: initiallySolved ? clues.map(c => c.id) : [],
        status: initiallySolved ? 'won' : 'playing',
    }), [initiallySolved, clues]);

    // Show confetti if initially solved
    useEffect(() => {
        if (initiallySolved) {
            setShowConfetti(true);
        }
    }, [initiallySolved]);

    const [state, setState] = useLocalStorage<Persisted>(`${storageKey}`, defaultState);
    const [gridAnswers, setGridAnswers] = useLocalStorage<Record<string, string>>(`${storageKey}:grid`, {});

    const isWon = state.status === 'won';

    // Build the crossword grid
    const grid = useMemo(() => {
        const newGrid: CellState[][] = [];

        // Initialize all cells as black
        for (let row = 0; row < 15; row++) {
            newGrid[row] = [];
            for (let col = 0; col < 15; col++) {
                newGrid[row][col] = { letter: '', isBlack: true };
            }
        }

        // Place words based on actual clues
        clues.forEach(clue => {
            const { startRow, startCol, direction, answer, number, id } = clue;

            for (let i = 0; i < answer.length; i++) {
                const row = direction === 'across' ? startRow : startRow + i;
                const col = direction === 'across' ? startCol + i : startCol;

                if (row < 15 && col < 15) {
                    // Mark cell as white and add clue info
                    newGrid[row][col].isBlack = false;

                    // Add clue number at the start of each word (only if no number exists or this is a lower number)
                    if (i === 0) {
                        if (!newGrid[row][col].number || number < newGrid[row][col].number!) {
                            newGrid[row][col].number = number;
                        }
                    }

                    // Add clue references (preserve existing ones)
                    if (direction === 'across') {
                        newGrid[row][col].acrossClue = id;
                    } else {
                        newGrid[row][col].downClue = id;
                    }
                }
            }
        });



        return newGrid;
    }, [clues]);

    // Get clues organized by direction
    const acrossClues = useMemo(() =>
        clues.filter(c => c.direction === 'across').sort((a, b) => a.number - b.number),
        [clues]
    );

    const downClues = useMemo(() =>
        clues.filter(c => c.direction === 'down').sort((a, b) => a.number - b.number),
        [clues]
    );

    const handleCellClick = useCallback((row: number, col: number) => {
        if (disabled || isWon || grid[row][col].isBlack) return;

        const cell = grid[row][col];

        if (selectedCell?.row === row && selectedCell?.col === col) {
            // Only toggle direction if both directions are available
            if (cell.acrossClue && cell.downClue) {
                const newDirection = currentDirection === 'across' ? 'down' : 'across';
                setCurrentDirection(newDirection);
            }
        } else {
            setSelectedCell({ row, col });

            // Always force direction based on what's available at this cell
            if (cell.acrossClue && cell.downClue) {
                // Both available - check if current direction is valid, otherwise pick the first available
                const currentCellClue = currentDirection === 'across' ? cell.acrossClue : cell.downClue;
                if (!currentCellClue) {
                    // Current direction not available at this cell, switch to available one
                    const newDirection = cell.acrossClue ? 'across' : 'down';
                    setCurrentDirection(newDirection);
                }
            } else if (cell.acrossClue && !cell.downClue) {
                setCurrentDirection('across');
            } else if (cell.downClue && !cell.acrossClue) {
                setCurrentDirection('down');
            }
        }
    }, [disabled, isWon, grid, selectedCell, currentDirection]);

    // Get the current word being worked on
    const currentWord = useMemo(() => {
        if (!selectedCell) return null;

        const { row, col } = selectedCell;
        const cell = grid[row][col];

        if (currentDirection === 'across' && cell.acrossClue) {
            const clue = clues.find(c => c.id === cell.acrossClue);
            if (clue) {
                return {
                    clue,
                    startCol: clue.startCol,
                    endCol: clue.startCol + clue.answer.length - 1,
                    row: clue.startRow
                };
            }
        } else if (currentDirection === 'down' && cell.downClue) {
            const clue = clues.find(c => c.id === cell.downClue);
            if (clue) {
                return {
                    clue,
                    startRow: clue.startRow,
                    endRow: clue.startRow + clue.answer.length - 1,
                    col: clue.startCol
                };
            }
        }

        return null;
    }, [selectedCell, currentDirection, grid, clues]);

    // Get the current active cell (where typing will go)
    const activeCell = useMemo(() => {
        return selectedCell;
    }, [selectedCell]);

    // Check if a cell contains a letter from the meta phrase (LAKE DISTRICT)
    const isMetaLetter = useCallback((row: number, col: number) => {
        if (!isWon) return false;

        // Find clues that contain letters from "LAKE DISTRICT"
        const lakeClue = clues.find(c => c.answer === 'LAKE');
        const districtClue = clues.find(c => c.answer === 'DISTRICT');

        // Check if this cell is part of LAKE
        if (lakeClue) {
            const lakeStartRow = lakeClue.startRow;
            const lakeStartCol = lakeClue.startCol;
            if (lakeClue.direction === 'across' && row === lakeStartRow &&
                col >= lakeStartCol && col < lakeStartCol + 4) {
                return true;
            }
        }

        // Check if this cell is part of DISTRICT
        if (districtClue) {
            const districtStartRow = districtClue.startRow;
            const districtStartCol = districtClue.startCol;
            if (districtClue.direction === 'across' && row === districtStartRow &&
                col >= districtStartCol && col < districtStartCol + 8) {
                return true;
            }
        }

        return false;
    }, [isWon, clues]);

    const handleClueClick = useCallback((clueId: string) => {
        if (disabled || isWon) return;

        const clue = clues.find(c => c.id === clueId);
        if (!clue) return;

        setSelectedCell({ row: clue.startRow, col: clue.startCol });
        setCurrentDirection(clue.direction);
        setHighlightedClue(clueId);

        // Clear highlight after a delay
        setTimeout(() => setHighlightedClue(null), 2000);
    }, [disabled, isWon, clues]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (disabled || isWon || !selectedCell) return;

        const { row, col } = selectedCell;

        if (e.key === 'Backspace') {
            if (gridAnswers[`${row}-${col}`]) {
                const newAnswers = { ...gridAnswers };
                delete newAnswers[`${row}-${col}`];
                setGridAnswers(newAnswers);
            }
            // Move to previous cell within the same word
            if (currentWord && currentWord.clue.direction === 'across' && currentWord.endCol !== undefined) {
                let prevCol = col - 1;
                while (prevCol >= currentWord.startCol && grid[row][prevCol].isBlack) prevCol--;
                if (prevCol >= currentWord.startCol) setSelectedCell({ row, col: prevCol });
            } else if (currentWord && currentWord.clue.direction === 'down' && currentWord.endRow !== undefined) {
                let prevRow = row - 1;
                while (prevRow >= currentWord.startRow && grid[prevRow][col].isBlack) prevRow--;
                if (prevRow >= currentWord.startRow) setSelectedCell({ row: prevRow, col });
            }
        } else if (e.key.match(/^[A-Za-z]$/)) {
            const newAnswers = { ...gridAnswers, [`${row}-${col}`]: e.key.toUpperCase() };
            setGridAnswers(newAnswers);

            // Move to next cell within the same word
            if (currentWord && currentWord.clue.direction === 'across' && currentWord.endCol !== undefined) {
                let nextCol = col + 1;
                while (nextCol <= currentWord.endCol && grid[row][nextCol].isBlack) nextCol++;
                if (nextCol <= currentWord.endCol) setSelectedCell({ row, col: nextCol });
            } else if (currentWord && currentWord.clue.direction === 'down' && currentWord.endRow !== undefined) {
                let nextRow = row + 1;
                while (nextRow <= currentWord.endRow && grid[nextRow][col].isBlack) nextRow++;
                if (nextRow <= currentWord.endRow) setSelectedCell({ row: nextRow, col });
            }
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            if (currentDirection === 'across' && currentWord && currentWord.endCol !== undefined) {
                let newCol = col;
                if (e.key === 'ArrowLeft') {
                    newCol = Math.max(currentWord.startCol, col - 1);
                    while (newCol > currentWord.startCol && grid[row][newCol].isBlack) newCol--;
                } else {
                    newCol = Math.min(currentWord.endCol, col + 1);
                    while (newCol < currentWord.endCol && grid[row][newCol].isBlack) newCol++;
                }
                if (!grid[row][newCol].isBlack) setSelectedCell({ row, col: newCol });
            }
        } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            if (currentDirection === 'down' && currentWord && currentWord.endRow !== undefined) {
                let newRow = row;
                if (e.key === 'ArrowUp') {
                    newRow = Math.max(currentWord.startRow, row - 1);
                    while (newRow > currentWord.startRow && grid[newRow][col].isBlack) newRow--;
                } else {
                    newRow = Math.min(currentWord.endRow, row + 1);
                    while (newRow < currentWord.endRow && grid[newRow][col].isBlack) newRow++;
                }
                if (!grid[newRow][col].isBlack) setSelectedCell({ row: newRow, col });
            }
        }
    }, [disabled, isWon, selectedCell, grid, gridAnswers, currentDirection, currentWord, setGridAnswers]);

    // Check if all clues are solved
    const checkCompletion = useCallback(() => {
        const allSolved = clues.every(clue => {
            const answer = clue.answer.toUpperCase();

            for (let i = 0; i < answer.length; i++) {
                const row = clue.direction === 'across' ? clue.startRow : clue.startRow + i;
                const col = clue.direction === 'across' ? clue.startCol + i : clue.startCol;
                const cellAnswer = gridAnswers[`${row}-${col}`];

                if (!cellAnswer || cellAnswer.toUpperCase() !== answer[i]) {
                    return false;
                }
            }

            return true;
        });

        if (allSolved && state.status !== 'won') {
            setState(prev => ({ ...prev, status: 'won' }));
            setShowConfetti(true);
            if (onSolved) onSolved();
        }
    }, [clues, gridAnswers, state.status, setState, onSolved]);

    // Check completion whenever grid answers change
    useEffect(() => {
        checkCompletion();
    }, [checkCompletion]);

    return (
        <div className={styles.container}>
            <div className={styles.gridContainer}>
                <div className={styles.directionIndicator}>
                    <span className={styles.directionText}>{currentDirection.toUpperCase()}</span>
                    {currentWord && (
                        <span className={styles.currentClue}>
                            {' '}• {currentWord.clue.number}. {currentWord.clue.clue}
                        </span>
                    )}
                    {selectedCell && grid[selectedCell.row] && grid[selectedCell.row][selectedCell.col] &&
                        grid[selectedCell.row][selectedCell.col].acrossClue &&
                        grid[selectedCell.row][selectedCell.col].downClue && (
                            <span className={styles.directionHint}>
                                {' '}• Click again to toggle direction
                            </span>
                        )}
                </div>
                <div className={styles.grid} tabIndex={0} onKeyDown={handleKeyDown}>
                    {grid.map((row, rowIndex) => (
                        <div key={rowIndex} className={styles.row}>
                            {row.map((cell, colIndex) => (
                                <div
                                    key={`${rowIndex}-${colIndex}`}
                                    className={`
                    ${styles.cell}
                    ${cell.isBlack ? styles.black : ''}
                    ${highlightedClue && (cell.acrossClue === highlightedClue || cell.downClue === highlightedClue) ? styles.highlighted : ''}
                    ${currentWord && currentWord.clue.direction === 'across' &&
                                            currentWord.row === rowIndex && colIndex >= currentWord.startCol && colIndex <= currentWord.endCol ? styles.currentWord : ''}
                    ${currentWord && currentWord.clue.direction === 'down' &&
                                            currentWord.col === colIndex && rowIndex >= currentWord.startRow && rowIndex <= currentWord.endRow ? styles.currentWord : ''}
                    ${activeCell?.row === rowIndex && activeCell?.col === colIndex ? styles.activeCell : ''}
                    ${isWon && isMetaLetter(rowIndex, colIndex) ? styles.metaLetter : ''}
                  `}
                                    onClick={() => handleCellClick(rowIndex, colIndex)}
                                >
                                    {cell.number && <span className={styles.number}>{cell.number}</span>}
                                    {!cell.isBlack && (
                                        <input
                                            className={styles.letterInput}
                                            value={gridAnswers[`${rowIndex}-${colIndex}`] || ''}
                                            onChange={(e) => {
                                                const value = e.target.value.toUpperCase();
                                                if (value.length <= 1 && /^[A-Z]?$/.test(value)) {
                                                    const newAnswers = { ...gridAnswers };
                                                    if (value === '') {
                                                        delete newAnswers[`${rowIndex}-${colIndex}`];
                                                    } else {
                                                        newAnswers[`${rowIndex}-${colIndex}`] = value;
                                                    }
                                                    setGridAnswers(newAnswers);

                                                    // Auto-advance to next cell if typing, but only within the same word
                                                    if (value !== '' && value.length === 1) {
                                                        setTimeout(() => {
                                                            if (currentDirection === 'across' && currentWord && currentWord.clue.direction === 'across' && currentWord.endCol !== undefined) {
                                                                // Only advance if we're still within the same word
                                                                const nextCol = colIndex + 1;
                                                                if (nextCol <= currentWord.endCol) {
                                                                    setSelectedCell({ row: rowIndex, col: nextCol });
                                                                }
                                                            } else if (currentDirection === 'down' && currentWord && currentWord.clue.direction === 'down' && currentWord.endRow !== undefined) {
                                                                // Only advance if we're still within the same word
                                                                const nextRow = rowIndex + 1;
                                                                if (nextRow <= currentWord.endRow) {
                                                                    setSelectedCell({ row: nextRow, col: colIndex });
                                                                }
                                                            }
                                                        }, 50);
                                                    }
                                                }
                                            }}
                                            disabled={disabled || isWon}
                                            maxLength={1}
                                            autoComplete="off"
                                            autoCorrect="off"
                                            autoCapitalize="off"
                                            spellCheck={false}
                                            readOnly
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>

            {/* Virtual keyboard for mobile */}
            <div className={styles.virtualKeyboard}>
                <VirtualKeyboard
                    onKeyPress={(key) => {
                        if (selectedCell && !disabled && !isWon) {
                            const { row, col } = selectedCell;
                            const newAnswers = { ...gridAnswers, [`${row}-${col}`]: key };
                            setGridAnswers(newAnswers);

                            // Auto-advance to next cell within the same word
                            if (currentWord && currentWord.clue.direction === 'across' && currentWord.endCol !== undefined) {
                                const nextCol = col + 1;
                                if (nextCol <= currentWord.endCol) {
                                    setSelectedCell({ row, col: nextCol });
                                }
                            } else if (currentWord && currentWord.clue.direction === 'down' && currentWord.endRow !== undefined) {
                                const nextRow = row + 1;
                                if (nextRow <= currentWord.endRow) {
                                    setSelectedCell({ row: nextRow, col });
                                }
                            }
                        }
                    }}
                    onDelete={() => {
                        if (selectedCell && !disabled && !isWon) {
                            const { row, col } = selectedCell;
                            const newAnswers = { ...gridAnswers };
                            delete newAnswers[`${row}-${col}`];
                            setGridAnswers(newAnswers);

                            // Move to previous cell within the same word
                            if (currentWord && currentWord.clue.direction === 'across' && currentWord.endCol !== undefined) {
                                let prevCol = col - 1;
                                while (prevCol >= currentWord.startCol && grid[row][prevCol].isBlack) prevCol--;
                                if (prevCol >= currentWord.startCol) setSelectedCell({ row, col: prevCol });
                            } else if (currentWord && currentWord.clue.direction === 'down' && currentWord.endRow !== undefined) {
                                let prevRow = row - 1;
                                while (prevRow >= currentWord.startRow && grid[prevRow][col].isBlack) prevRow--;
                                if (prevRow >= currentWord.startRow) setSelectedCell({ row: prevRow, col });
                            }
                        }
                    }}
                    disabled={disabled || isWon}
                    showEnter={false}
                    deleteDisabled={!selectedCell}
                />
            </div>

            <div className={styles.cluesContainer}>
                <div className={styles.cluesSection}>
                    <h3>Across</h3>
                    <ol className={styles.clueList}>
                        {acrossClues.map((clue) => (
                            <li
                                key={clue.id}
                                className={`${styles.clue} ${highlightedClue === clue.id ? styles.highlighted : ''}`}
                                onClick={() => handleClueClick(clue.id)}
                            >
                                <span className={styles.clueNumber}>{clue.number}.</span>
                                <span className={styles.clueText}>{clue.clue}</span>
                            </li>
                        ))}
                    </ol>
                    <h3>Down</h3>
                    <ol className={styles.clueList}>
                        {downClues.map((clue) => (
                            <li
                                key={clue.id}
                                className={`${styles.clue} ${highlightedClue === clue.id ? styles.highlighted : ''}`}
                                onClick={() => handleClueClick(clue.id)}
                            >
                                <span className={styles.clueNumber}>{clue.number}.</span>
                                <span className={styles.clueText}>{clue.clue}</span>
                            </li>
                        ))}
                    </ol>
                </div>
            </div>

            <Confetti isActive={showConfetti} duration={3000} />
        </div>
    );
};

export default CrosswordStep;


